/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { FocusEventHandler } from "react";
import { useMemo, useRef, useState } from "react";
import type {
  FieldErrors,
  FieldValues,
  Resolver,
  ResolverOptions,
} from "react-hook-form";

import type { ThemeType } from "../../global/emotion";
import { lineHeights } from "../../global/emotion";
import type { HallowElementProps } from "../types/hallowElementProps.types";

const errorColorStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.errorError,
});
const errorTextStyle: Interpolation = {
  minHeight: `calc(${lineHeights.detailXL} * 2)`, // errors can go to 2 lines easily
  opacity: 0,
  transition: "opacity 0ms ease-in-out",
  willChange: "opacity",
};
const visibleErrorStyle: Interpolation = {
  opacity: 1,
  transition: "opacity 100ms ease-in-out",
};

export const useValidator = <TFieldValues extends FieldValues>(
  resolver: Resolver<TFieldValues>,
): {
  resolver: Resolver<TFieldValues>;
  fieldsCSS: Record<keyof TFieldValues, Interpolation>;
  errorsCSS: Record<keyof TFieldValues, Interpolation>;
  errors: FieldErrors<TFieldValues>;
  inputProps: Partial<HallowElementProps<"input">>;
  showError: Partial<Record<keyof TFieldValues, boolean>>;
} => {
  const [errors, setErrors] = useState<FieldErrors<TFieldValues>>(null);
  const [fields, setFields] = useState<Record<string, any>>(null);
  const [showError, setShowError] = useState<Record<string, boolean>>({});
  const touched = useRef<Record<string, boolean>>({});

  const fieldsCSS = useMemo(() => {
    const arrays: Record<string, Interpolation> = {};
    for (const field in errors) {
      if (showError?.[field]) arrays[field] = errorColorStyle;
    }
    return arrays;
  }, [errors, showError]);

  const errorsCSS = useMemo(() => {
    const arrays: Record<string, Interpolation> = {};
    for (const field in fields) {
      arrays[field] = [
        errorColorStyle,
        errorTextStyle,
        !!errors?.[field] && showError?.[field] && visibleErrorStyle,
      ];
    }
    return arrays;
  }, [fields, errors, showError]);

  const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    const field = e.currentTarget.name;
    touched.current[field] = true;
    setShowError((prev) => ({ ...prev, [field]: false }));
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    const field = e.currentTarget.name;
    if (touched.current[field]) {
      setShowError((prev) => ({ ...prev, [field]: true }));
    }
  };

  const forwardedResolver = async (
    values: TFieldValues,
    context: any,
    options: ResolverOptions<TFieldValues>,
  ) => {
    const results = await resolver(values, context, options);
    setErrors(results.errors);
    setFields(options.fields);
    return results;
  };

  return {
    resolver: forwardedResolver,
    fieldsCSS: fieldsCSS as Record<keyof TFieldValues, Interpolation>,
    errorsCSS: errorsCSS as Record<keyof TFieldValues, Interpolation>,
    errors,
    inputProps: {
      onFocus: handleFocus,
      onBlurCapture: handleBlur,
    },
    showError: showError as Partial<Record<keyof TFieldValues, boolean>>,
  };
};
